import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import apiClient from './apiClient';
import { getFeriadosDelAnio } from './api.service.js';
import { getFechasInhabilitadas } from './fechas_inhabilitadas.service.js';
import { getFechasInhabilitadasFin } from './fechas_inhabilitadas_fin.service.js';
import { getRestricciones } from './restricciones.service.js';

dayjs.extend(utc);
dayjs.extend(timezone);

const URL = process.env.REACT_APP_API_URL + "/instancias";

// Caché en memoria y deduplicación de peticiones en vuelo para las restricciones de fechas del calendario
const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutos
let restrictionsCache = null;
let restrictionsCacheExpiresAt = 0;
let inFlightRestrictionsPromise = null;
const instanciasPorCursoCache = new Map();

export const invalidateCalendarioRestriccionesCache = () => {
    restrictionsCache = null;
    restrictionsCacheExpiresAt = 0;
    inFlightRestrictionsPromise = null;
    instanciasPorCursoCache.clear();
};

export const getCachedCalendarioRestricciones = () => {
    if (restrictionsCache && Date.now() < restrictionsCacheExpiresAt) {
        return restrictionsCache;
    }
    return null;
};

export const getCalendarioRestricciones = async (forceRefresh = false) => {
    if (forceRefresh) {
        restrictionsCache = null;
        restrictionsCacheExpiresAt = 0;
    } else {
        const cached = getCachedCalendarioRestricciones();
        if (cached) {
            return cached;
        }
        if (inFlightRestrictionsPromise) {
            return inFlightRestrictionsPromise;
        }
    }

    const ahoraArgentina = dayjs().tz('America/Argentina/Buenos_Aires');
    const anioActual = ahoraArgentina.year().toString();
    const anioSiguiente = ahoraArgentina.add(1, 'year').year().toString();

    inFlightRestrictionsPromise = (async () => {
        try {
            const [
                fechasInvalidasActual,
                fechasInvalidasSiguiente,
                fechasInhabilitadas,
                fechasInhabilitadasFin,
                feriados,
                restriccionesData,
            ] = await Promise.all([
                getFechasInvalidas(anioActual),
                getFechasInvalidas(anioSiguiente),
                getFechasInhabilitadas(),
                getFechasInhabilitadasFin(),
                getFeriadosDelAnio(),
                getRestricciones().catch(() => null),
            ]);

            const fechasInvalidas = [
                ...(Array.isArray(fechasInvalidasActual) ? fechasInvalidasActual : []),
                ...(Array.isArray(fechasInvalidasSiguiente) ? fechasInvalidasSiguiente : []),
            ];
            const safeFechasInhabilitadas = Array.isArray(fechasInhabilitadas) ? fechasInhabilitadas : [];
            const safeFechasInhabilitadasFin = Array.isArray(fechasInhabilitadasFin) ? fechasInhabilitadasFin : [];
            const safeFeriados = Array.isArray(feriados) ? feriados : [];
            const rawVentanaInicio = Number(restriccionesData?.restriccion?.ventanaInicio ?? 0);
            const ventanaInicio = Number.isNaN(rawVentanaInicio) ? 0 : rawVentanaInicio;

            const result = {
                fechasInvalidas,
                fechasInhabilitadas: safeFechasInhabilitadas,
                fechasInhabilitadasFin: safeFechasInhabilitadasFin,
                feriados: safeFeriados,
                ventanaInicio,
                fechasInvalidasSet: new Set(fechasInvalidas.map((el) => el.calendario_fecha)),
                fechasInhabilitadasSet: new Set(safeFechasInhabilitadas.map((el) => el.fecha)),
                fechasInhabilitadasFinSet: new Set(safeFechasInhabilitadasFin.map((el) => el.fecha)),
                feriadosSet: new Set(safeFeriados.map((el) => el.fecha)),
            };

            restrictionsCache = result;
            restrictionsCacheExpiresAt = Date.now() + CACHE_TTL_MS;
            return result;
        } finally {
            inFlightRestrictionsPromise = null;
        }
    })();

    return inFlightRestrictionsPromise;
};

export const postInstancias = async (newInstancia) => {
    try {
        const response = await apiClient(URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(newInstancia)
        });
        const data = await response.json();
        if (response.status !== 201) {
            throw new Error(data.message || "Error al registrar instancia");
        }

        invalidateCalendarioRestriccionesCache();
        return data;
    } catch (error) {
        throw error;
    }
};

export const getInstancias = async () => {
    try {
        const response = await apiClient(URL, {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            }
        });
        const data = await response.json();

        if (response.status !== 200) {
            throw new Error(data.message || "Error al buscar instancia");
        }

        if (!data || data.length === 0) {
            throw new Error("No se encontraron instancias");
        }
        return data;
    } catch (error) {
        throw error;
    }
};

export const getInstanciasByCurso = async (curso) => {
    if (!curso) return [];
    if (instanciasPorCursoCache.has(curso)) {
        return instanciasPorCursoCache.get(curso);
    }

    try {
        const response = await apiClient(`${URL}?curso=${encodeURIComponent(curso)}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            }
        });
        const data = await response.json();

        if (response.status !== 200) {
            throw new Error(data.message || "Error al buscar instancia");
        }

        if (!data || !Array.isArray(data) || data.length === 0) {
            instanciasPorCursoCache.set(curso, []);
            return [];
        }

        const filtradas = data.filter(instancia => instancia.curso === curso && instancia.estado_instancia !== 'CANC');
        instanciasPorCursoCache.set(curso, filtradas);
        return filtradas;
    } catch (error) {
        console.error("Error obteniendo instancias por curso:", error);
        return [];
    }
};

export const getFechasInvalidas = async (targetYear) => {
    try {
        const response = await apiClient(`${URL}/get-fechas-invalidas/${targetYear}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            }
        });

        const data = await response.json();
        if (response.status !== 200) {
            throw new Error(data.message || "Error al registrar instancia");
        }
        return data;
    } catch (error) {
        throw error;
    }
};

export const putInstancia = async (curso_params, fecha_inicio_curso_params, newInstancia) => {
    try {
        const response = await apiClient(`${URL}/${curso_params}/${fecha_inicio_curso_params}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(newInstancia)
        });
        const data = await response.json();
        if (response.status !== 200) {
            throw new Error(data.message || "Error al actualizar instancia");
        }
        invalidateCalendarioRestriccionesCache();
        return data;
    } catch (error) {
        throw error;
    }
};

export const putInstanciasMasivo = async (payload) => {
    try {
        const response = await apiClient(`${URL}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (response.status !== 200) {
            throw new Error(data.message || "Error al actualizar instancias masivamente");
        }
        invalidateCalendarioRestriccionesCache();
        return data;
    } catch (error) {
        throw error;
    }
};

export const getTablaInstanciasPorUsuario = async (fecha_desde, fecha_hasta) => {
    try {
        const response = await apiClient(`${URL}/tabla-instancias-por-usuario?fecha_desde=${fecha_desde}&fecha_hasta=${fecha_hasta}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            }
        });
        const data = await response.json();

        if (response.status !== 200) {
            throw new Error(data.message || "Error al buscar tabla de instancias por usuario");
        }

        return data;
    } catch (error) {
        throw error;
    }
};
